import { createContext, ReactNode, useEffect, useState } from "react"
import { axiosInstance } from "api/axios.instance"
import { useNavigate } from "react-router-dom"
import { AxiosError } from "axios"
import Cookies from "js-cookie"

interface AuthContextType {
	isAuthenticated: boolean
	logout: () => Promise<void>
	setToken: (token: string) => void
}

export const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const navigate = useNavigate()
	const [isAuthenticated, setIsAuthenticated] = useState(() => !!Cookies.get("token"))

	const setToken = (token: string) => {
		Cookies.set("token", token)
		setIsAuthenticated(true)
	}

	const clearToken = () => {
		Cookies.remove("token")
		setIsAuthenticated(false)
	}

	const logout = async () => {
		try {
			// Your API call to destroy token if needed
			// await api.post('/auth/logout');
			clearToken()
			navigate("/")
		} catch (error) {
			console.error("Logout failed:", error)
			clearToken()
			navigate("/")
		}
	}

	useEffect(() => {
		// Set up axios interceptor for 401 errors and logout user when received 401 Unauthorized
		const interceptor = axiosInstance.interceptors.response.use(
			(response) => response,
			async (error: AxiosError) => {
				if (error.response?.status === 401) {
					clearToken()
					setIsAuthenticated(false)
					navigate("/")
				}
				return Promise.reject(error)
			},
		)

		return () => {
			axiosInstance.interceptors.response.eject(interceptor)
		}
	}, [navigate])

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				logout,
				setToken,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}
